import { useRef, useState, type SyntheticEvent } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import './profile.css';

interface ProfilePhotoCropModalProps {
  imageUrl: string;
  onClose: () => void;
  onSaved: () => void;
}

const PROFILE_CROP_ASPECT = 1;

export const profileCropInPixels = (
  crop: Crop,
  image: Pick<HTMLImageElement, 'naturalWidth' | 'naturalHeight' | 'width' | 'height'>,
): PixelCrop => {
  if (crop.unit === '%') {
    return {
      unit: 'px',
      x: Math.round((crop.x / 100) * image.naturalWidth),
      y: Math.round((crop.y / 100) * image.naturalHeight),
      width: Math.max(1, Math.round((crop.width / 100) * image.naturalWidth)),
      height: Math.max(1, Math.round((crop.height / 100) * image.naturalHeight)),
    };
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  return {
    unit: 'px',
    x: Math.round(crop.x * scaleX),
    y: Math.round(crop.y * scaleY),
    width: Math.max(1, Math.round(crop.width * scaleX)),
    height: Math.max(1, Math.round(crop.height * scaleY)),
  };
};

const needsCors = (uri: string): boolean => {
  if (/^(?:blob|data):/i.test(uri)) return false;
  try {
    return new URL(uri, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
};

export function ProfilePhotoCropModal({
  imageUrl,
  onClose,
  onSaved,
}: ProfilePhotoCropModalProps) {
  const { t } = useTranslation();
  const { updateProfile } = useProfile();
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [corsFallback, setCorsFallback] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const requestCors = !corsFallback && needsCors(imageUrl);

  const handleLoad = (event: SyntheticEvent<HTMLImageElement>): void => {
    const image = event.currentTarget;
    imageRef.current = image;
    setCrop(centerCrop(
      makeAspectCrop(
        { unit: '%', width: 90 },
        PROFILE_CROP_ASPECT,
        image.width,
        image.height,
      ),
      image.width,
      image.height,
    ));
  };

  const saveCrop = async (): Promise<void> => {
    if (!crop || !imageRef.current || saving) return;

    setSaving(true);
    setError('');
    try {
      const image = imageRef.current;
      const selected = profileCropInPixels(crop, image);
      const size = Math.min(selected.width, selected.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas is unavailable.');

      context.drawImage(
        image,
        selected.x,
        selected.y,
        selected.width,
        selected.height,
        0,
        0,
        size,
        size,
      );

      const blob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.92);
      });
      if (!blob) throw new Error('Crop export failed.');

      const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
      await updateProfile({
        avatarBlob: file,
      });
      ToastService.show({
        type: 'success',
        message: t('profile.photoViewer.cropSaved'),
      });
      onSaved();
    } catch {
      setError(t('profile.photoViewer.cropFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t('profile.photoViewer.editPhoto')}
      labelledBy="profile-photo-crop-title"
      className="profile-photo-crop-modal"
      onClose={onClose}
    >
      <div className="profile-photo-crop">
        <p>{t('profile.photoViewer.cropDescription')}</p>
        <div className="profile-photo-crop__canvas">
          <ReactCrop
            crop={crop}
            aspect={PROFILE_CROP_ASPECT}
            circularCrop
            keepSelection
            onChange={(_, percentCrop) => setCrop(percentCrop)}
          >
            <img
              key={requestCors ? 'cors' : 'plain'}
              ref={imageRef}
              src={imageUrl}
              crossOrigin={requestCors ? 'anonymous' : undefined}
              alt={t('profile.photoViewer.cropPreview')}
              onLoad={handleLoad}
              onError={() => {
                if (requestCors) setCorsFallback(true);
              }}
            />
          </ReactCrop>
        </div>
        <FormError variant="banner" message={error} />
        <div className="profile-photo-crop__footer">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            onClick={() => {
              saveCrop().catch(() => undefined);
            }}
            disabled={!crop || saving}
          >
            {t(saving ? 'profile.photoViewer.savingCrop' : 'profile.photoViewer.saveCrop')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
